import React, { useState } from 'react';
import '../styles/MoveHistory.css';

const MoveHistory = ({ moves, currentMoveIndex }) => {
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  // Convert position index to algebraic notation
  const indexToSquare = (index) => {
    const file = String.fromCharCode(97 + (index % 8)); // a-h
    const rank = Math.floor(index / 8) + 1; // 1-8
    return `${file}${rank}`;
  };

  // Get piece symbol for notation
  const getPieceSymbol = (pieceType) => {
    const symbols = {
      'king': 'K',
      'queen': 'Q',
      'rook': 'R',
      'bishop': 'B',
      'knight': 'N',
      'pawn': '',
      'duke': 'D'
    };
    return symbols[pieceType] || '';
  };

  // Format move in standard notation
  const formatMove = (move) => {
    if (!move) return '';

    const { piece, from, to, captured, promotion, isCheck, isCheckmate, isCastling } = move;

    // Castling notation
    if (isCastling) {
      // Kingside castling
      if (to > from) return 'O-O';
      // Queenside castling
      return 'O-O-O';
    }

    let notation = '';

    // Piece symbol (empty for pawns)
    const pieceSymbol = getPieceSymbol(piece?.type);
    notation += pieceSymbol;

    // Starting square (only for captures with pawns or ambiguous moves)
    const fromSquare = indexToSquare(from);
    
    // Capture indicator
    if (captured) {
      // For pawn captures, include the starting file
      if (!pieceSymbol) {
        notation += fromSquare[0];
      }
      notation += 'x';
    }

    // Destination square
    notation += indexToSquare(to);

    // Promotion
    if (promotion) {
      notation += '=' + getPieceSymbol(promotion);
    }

    // Check or Checkmate
    if (isCheckmate) {
      notation += '#';
    } else if (isCheck) {
      notation += '+';
    }

    return notation;
  };

  // Group moves into pairs (white, black)
  const movePairs = [];
  for (let i = 0; i < moves.length; i += 2) {
    movePairs.push({
      moveNumber: Math.floor(i / 2) + 1,
      white: moves[i],
      black: moves[i + 1] || null
    });
  }

  return (
    <>
      {/* Mobile toggle button */}
      <button 
        className="move-history-toggle"
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        data-testid="move-history-toggle"
      >
        📜
      </button>

      <div className={`move-history-sidebar ${isMobileOpen ? 'mobile-open' : ''}`}>
        <div className="move-history-header">
          <h3>📜 Move History</h3>
          <button 
            className="mobile-close-btn"
            onClick={() => setIsMobileOpen(false)}
          >
            ×
          </button>
        </div>
        
        <div className="move-history-content">
          {movePairs.length === 0 ? (
            <div className="no-moves">
              <p>No moves yet</p>
              <p className="hint">Moves will appear here as you play</p>
            </div>
          ) : (
            <div className="moves-list">
              {movePairs.map((pair) => (
                <div 
                  key={`move-${pair.moveNumber}`} 
                  className={`move-pair ${currentMoveIndex >= (pair.moveNumber - 1) * 2 ? 'played' : 'future'}`}
                >
                  <div className="move-number">{pair.moveNumber}.</div>
                  <div className="move-white">
                    {formatMove(pair.white)}
                  </div>
                  <div className="move-black">
                    {pair.black ? formatMove(pair.black) : '...'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="move-history-footer">
          <div className="move-count">
            Total moves: {moves.length}
          </div>
        </div>
      </div>
    </>
  );
};

export default MoveHistory;
